import { SearchItem } from "./types";

export const searchRegistry: SearchItem[] = [
  // ============ PAGES ============
  {
    id: "dashboard",
    title: "Dashboard",
    description: "View your dashboard overview",
    keywords: ["dashboard", "home", "overview", "main", "start", "analytics", "summary", "landing"],
    category: "Pages",
    path: "/",
    icon: "home",
  },
  {
    id: "agents",
    title: "Agents",
    description: "Manage your AI agents",
    keywords: ["agents", "bots", "voice", "assistant", "list", "ai", "manage", "all agents", "my agents"],
    category: "Pages",
    path: "/agents",
    icon: "bot",
  },
  {
    id: "create-agent",
    title: "Create Agent",
    description: "Create a new AI agent",
    keywords: ["create", "new", "add", "build", "agent", "setup", "configure", "start", "make", "start new"],
    category: "Pages",
    path: "/agents/new",
    icon: "add",
  },
  {
    id: "simulations",
    title: "Simulations",
    description: "Run agent simulations",
    keywords: ["simulations", "test", "simulate", "run", "batch", "mock", "load test", "stress test", "regression", "scenario"],
    category: "Pages",
    path: "/agents/simulations",
    icon: "play",
    adminOnly: true,
  },
  {
    id: "create-simulation",
    title: "Create Simulation",
    description: "Create a new simulation",
    keywords: ["create", "new", "add", "simulation", "test", "mock", "batch run"],
    category: "Pages",
    path: "/agents/simulations/new",
    icon: "add",
    adminOnly: true,
  },
  {
    id: "evaluators",
    title: "Evaluators",
    description: "Manage evaluators",
    keywords: ["evaluators", "test", "quality", "check", "score", "grading", "benchmark", "accuracy", "assess", "rubric", "assessment"],
    category: "Pages",
    path: "/evaluators",
    icon: "checklist",
    adminOnly: true,
  },
  {
    id: "create-evaluator",
    title: "Create Evaluator",
    description: "Create a new evaluator",
    keywords: ["create", "new", "add", "evaluator", "quality", "scoring", "test quality", "rubric"],
    category: "Pages",
    path: "/evaluators/new",
    icon: "add",
    adminOnly: true,
  },
  {
    id: "organization",
    title: "Organization",
    description: "Organization settings and members",
    keywords: ["organization", "team", "company", "workspace", "settings", "org", "account", "manage team"],
    category: "Pages",
    path: "/organization/members",
    icon: "users",
  },
  {
    id: "voice-lab",
    title: "Voice Lab",
    description: "Test and compare TTS voices from any provider",
    keywords: [
      "voice",
      "lab",
      "voices",
      "audio",
      "speech",
      "tts",
      "text-to-speech",
      "compare",
      "test",
      "provider",
      "playground",
      "preview",
      "listen",
      "sample",
      "try",
      "elevenlabs",
      "deepgram",
      "playht",
    ],
    category: "Pages",
    path: "/voice-lab",
    icon: "voice-over",
  },
  {
    id: "stt-lab",
    title: "STT Lab",
    description: "Test and compare speech-to-text models",
    keywords: [
      "stt",
      "lab",
      "speech",
      "transcription",
      "transcribe",
      "voice",
      "recognition",
      "test",
      "compare",
      "models",
      "speech to text",
      "whisper",
      "deepgram",
      "playground",
      "upload audio",
    ],
    category: "Pages",
    path: "/sttlab",
    icon: "mic",
  },
  {
    id: "llm-lab",
    title: "LLM Lab",
    description: "Compare LLM latency by model — TTFT, ms/token, sample counts",
    keywords: ["llm", "lab", "latency", "ttft", "tokens", "models", "compare", "performance", "openai", "anthropic", "google"],
    category: "Pages",
    path: "/llm-lab",
    icon: "bar_chart",
  },
  {
    id: "resources",
    title: "Resources / Documentation",
    description: "Documentation and resources",
    keywords: ["resources", "docs", "help", "guide", "documentation", "learn", "tutorial", "api docs", "getting started", "faq", "support", "reference"],
    category: "Pages",
    path: "/resources",
    icon: "folder_open",
  },
  {
    id: "profile",
    title: "Profile",
    description: "Your account settings",
    keywords: ["profile", "account", "user", "settings", "me", "password", "email", "security", "my account", "preferences"],
    category: "Pages",
    path: "/profile",
    icon: "person",
  },
  {
    id: "notifications",
    title: "Notifications",
    description: "View your notifications",
    keywords: ["notifications", "alerts", "messages", "inbox", "bell", "updates", "unread"],
    category: "Pages",
    path: "/notifications",
    icon: "notification",
  },
  {
    id: "notification-preferences",
    title: "Notification Preferences",
    description: "Configure notification settings",
    keywords: ["notification", "preferences", "settings", "alerts", "email", "subscribe", "unsubscribe", "mute", "digest", "configure"],
    category: "Pages",
    path: "/notification-preferences",
    icon: "settings",
  },
  {
    id: "style-guide",
    title: "Style Guide",
    description: "Design system components",
    keywords: ["style", "guide", "design", "components", "ui", "styles"],
    category: "Pages",
    path: "/style-guide",
    icon: "brush",
    adminOnly: true,
  },
  {
    id: "admin-users",
    title: "Admin: Users",
    description: "Manage platform users",
    keywords: ["admin", "users", "manage", "accounts"],
    category: "Pages",
    path: "/admin/users",
    icon: "users",
    adminOnly: true,
  },
  {
    id: "admin-organizations",
    title: "Admin: Organizations",
    description: "Manage organizations",
    keywords: ["admin", "organizations", "orgs", "manage", "workspaces"],
    category: "Pages",
    path: "/admin/organizations",
    icon: "grid",
    adminOnly: true,
  },

  // ============ AGENT FEATURES (Tabs) ============
  {
    id: "agent-overview",
    title: "Agent Overview",
    description: "View agent details, analytics, and configuration",
    keywords: [
      "agent",
      "overview",
      "details",
      "info",
      "analytics",
      "metrics",
      "stats",
      "statistics",
      "charts",
      "data",
      "summary",
      "status",
      "dashboard",
      "config",
      "performance",
    ],
    category: "Agent Features",
    path: "/agents/:agentId/overview",
    icon: "bot",
  },
  {
    id: "agent-function",
    title: "Agent Function",
    description: "Edit agent code and logic",
    keywords: [
      "agent",
      "function",
      "code",
      "editor",
      "logic",
      "script",
      "handler",
      "python",
      "edit code",
      "prompt",
      "llm",
      "model",
      "write code",
      "programming",
      "javascript",
      "typescript",
    ],
    category: "Agent Features",
    path: "/agents/:agentId/function",
    icon: "code",
  },
  {
    id: "agent-environments",
    title: "Agent Environments",
    description: "Configure agent environments",
    keywords: [
      "agent",
      "environments",
      "env",
      "config",
      "variables",
      "settings",
      "production",
      "staging",
      "dev",
      "prod",
      "keys",
      "secrets",
      "configuration",
      "development",
    ],
    category: "Agent Features",
    path: "/agents/:agentId/environments",
    icon: "layers",
  },
  {
    id: "agent-sessions",
    title: "Agent Sessions",
    description: "View conversation sessions and calls",
    keywords: [
      "agent",
      "sessions",
      "calls",
      "conversations",
      "history",
      "logs",
      "trace",
      "traces",
      "events",
      "transcript",
      "transcripts",
      "evaluations",
      "call history",
      "recordings",
      "listen",
      "playback",
      "debug",
      "phone calls",
      "call logs",
      "session details",
      "chat logs",
    ],
    category: "Agent Features",
    path: "/agents/:agentId/sessions",
    icon: "message",
  },
  {
    id: "agent-deployments",
    title: "Agent Deployments",
    description: "Deploy agent to channels",
    keywords: ["agent", "deployments", "deploy", "publish", "channels", "live", "release", "go live", "production", "ship"],
    category: "Agent Features",
    path: "/agents/:agentId/deployments",
    icon: "rocket",
  },
  {
    id: "agent-releases",
    title: "Agent Releases",
    description: "View this agent's releases across environments",
    keywords: ["agent", "releases", "release", "deploy", "history", "rollout", "version", "versions", "changelog", "ship", "promote", "rollback"],
    category: "Agent Features",
    path: "/agents/:agentId/releases",
    icon: "rocket_launch",
  },
  {
    id: "agent-testing",
    title: "Agent Testing",
    description: "Test agent functionality",
    keywords: [
      "agent",
      "testing",
      "test",
      "debug",
      "try",
      "playground",
      "sandbox",
      "a/b testing",
      "ab testing",
      "ab",
      "qa",
      "validate",
      "experiment",
      "experiments",
      "compare",
      "variant",
    ],
    category: "Agent Features",
    path: "/agents/:agentId/testing",
    icon: "testing",
  },
  {
    id: "agent-evaluation",
    title: "Agent Evaluation",
    description: "Evaluate agent performance",
    keywords: ["agent", "evaluation", "quality", "score", "metrics", "performance", "grading", "assess", "benchmark", "accuracy", "results"],
    category: "Agent Features",
    path: "/agents/:agentId/evaluation",
    icon: "checklist",
  },

  {
    id: "agent-metrics",
    title: "Agent Custom Metrics",
    description: "View custom metrics and time-series charts for an agent",
    keywords: [
      "agent",
      "metrics",
      "custom",
      "prometheus",
      "charts",
      "timeseries",
      "monitoring",
      "dashboard",
      "analytics",
      "graphs",
      "performance",
      "latency",
      "p50",
      "p99",
      "grafana",
    ],
    category: "Agent Features",
    path: "/agents/:agentId/metrics",
    icon: "activity",
  },

  // ============ AGENT SETTINGS (Dialogs) ============
  {
    id: "webhooks",
    title: "Webhooks",
    description: "Configure webhooks for agent environments",
    keywords: [
      "webhooks",
      "webhook",
      "http",
      "callback",
      "integration",
      "api",
      "events",
      "endpoint",
      "notification",
      "post",
      "event hook",
      "trigger",
      "url",
      "notify",
    ],
    category: "Agent Settings",
    path: "/agents/:agentId/environments?openModal=webhook",
    icon: "link",
  },
  {
    id: "startup-settings",
    title: "Startup Settings",
    description: "Configure initial agent behavior",
    keywords: [
      "startup",
      "settings",
      "greeting",
      "init",
      "initial",
      "welcome",
      "connection timeout",
      "startup audio snippet",
      "interruptible",
      "recording",
      "first message",
      "prompt",
      "opening",
      "startup audio",
    ],
    category: "Agent Settings",
    path: "/agents/:agentId/environments?openModal=startup",
    icon: "zap",
  },
  {
    id: "error-fallback",
    title: "Error Fallback Settings",
    description: "Configure error handling and failover",
    keywords: ["error", "fallback", "failover", "recovery", "failure", "retry", "timeout", "crash", "disconnect", "hang up", "backup", "graceful", "exception"],
    category: "Agent Settings",
    path: "/agents/:agentId/environments?openModal=fallback",
    icon: "error",
  },
  {
    id: "privacy-settings",
    title: "Privacy Settings",
    description: "Configure PII redaction and privacy",
    keywords: [
      "privacy",
      "settings",
      "pii",
      "redaction",
      "gdpr",
      "data",
      "trace",
      "tracing",
      "traces",
      "recording",
      "consent",
      "mask",
      "sensitive",
      "hipaa",
      "compliance",
      "anonymize",
    ],
    category: "Agent Settings",
    path: "/agents/:agentId/environments?openModal=privacy",
    icon: "shield",
  },
  {
    id: "stt-settings",
    title: "STT Settings",
    description: "Configure speech-to-text settings",
    keywords: [
      "stt",
      "settings",
      "speech",
      "transcription",
      "voice",
      "recognition",
      "model",
      "endpointing",
      "audio input delay",
      "input delay",
      "stt language",
      "language",
      "auto switch",
      "stt auto switch",
      "prewarm",
      "stt filter",
      "stt keywords",
      "eot",
      "vad",
      "noise reduction",
      "speech-to-text",
    ],
    category: "Agent Settings",
    path: "/agents/:agentId/environments?openModal=stt",
    icon: "mic",
  },
  {
    id: "region-settings",
    title: "Region Settings",
    description: "Configure region-specific settings",
    keywords: ["region", "settings", "location", "data center", "server", "latency", "aws", "cloud", "proximity", "us", "eu", "geography", "asia", "singapore"],
    category: "Agent Settings",
    path: "/agents/:agentId/environments?openModal=region",
    icon: "language",
  },

  // ============ ORGANIZATION FEATURES ============
  {
    id: "organization-settings",
    title: "Organization Settings",
    description: "Edit organization name and view current plan",
    keywords: [
      "organization",
      "name",
      "rename",
      "plan",
      "tier",
      "enterprise",
      "upgrade",
      "settings",
      "company name",
      "billing plan",
      "subscription",
      "workspace name",
    ],
    category: "Organization",
    path: "/organization/configurations#organization",
    icon: "settings",
  },
  {
    id: "api-keys",
    title: "API Keys",
    description: "Manage your API keys",
    keywords: [
      "api",
      "keys",
      "tokens",
      "authentication",
      "access",
      "credentials",
      "secret",
      "bearer",
      "generate key",
      "create key",
      "rotate",
      "service account",
      "personal access token",
      "pat",
    ],
    category: "Organization",
    path: "/organization/configurations#global-variables",
    icon: "key",
  },
  {
    id: "team-members",
    title: "Team Members",
    description: "View and manage team members",
    keywords: ["team", "members", "users", "people", "colleagues", "roles", "permissions", "admin", "remove member", "staff", "invite", "owner"],
    category: "Organization",
    path: "/organization/members",
    icon: "users",
  },
  {
    id: "invite-members",
    title: "Invite Members",
    description: "Invite new team members",
    keywords: ["invite", "members", "add", "team", "new", "onboard", "colleague", "email invitation", "join", "send invite"],
    category: "Organization",
    path: "/organization/members",
    icon: "invite",
  },
  {
    id: "telephony-settings",
    title: "Telephony Settings",
    description: "Configure Twilio and Telnyx",
    keywords: ["telephony", "phone", "twilio", "telnyx", "calling", "sip", "phone number", "inbound", "outbound", "voip", "carrier", "provider"],
    category: "Organization",
    path: "/organization/configurations#telephony-configurations",
    icon: "phone",
  },
  {
    id: "global-variables",
    title: "Global Variables",
    description: "Manage organization environment variables",
    keywords: [
      "global",
      "variables",
      "env",
      "environment",
      "config",
      "secrets",
      "shared config",
      "tokens",
      "api keys",
      "organization config",
      "organization variables",
    ],
    category: "Organization",
    path: "/organization/configurations#global-variables",
    icon: "list",
  },
  {
    id: "organization-environments",
    title: "Environments",
    description: "Manage organization deployment environments",
    keywords: ["environments", "env", "production", "staging", "dev", "deploy", "release", "create environment", "workspace", "create env", "org environments"],
    category: "Organization",
    path: "/organization/configurations#environments",
    icon: "rocket",
  },
  {
    id: "organization-entrypoints",
    title: "Entrypoints",
    description: "Manage organization entrypoints (phone, web, native)",
    keywords: [
      "entrypoints",
      "entrypoint",
      "ingress",
      "phone",
      "web",
      "native",
      "routing",
      "create entrypoint",
      "phone number",
      "widget",
      "embed",
      "sdk",
      "connect",
      "assign",
      "channel",
      "integration",
    ],
    category: "Organization",
    path: "/organization/configurations#entrypoints",
    icon: "link",
  },
  {
    id: "custom-voices",
    title: "Custom Voices",
    description: "Manage custom voice clones",
    keywords: ["custom", "voices", "voice", "clone", "tts", "audio", "my voice", "upload voice", "voice synthesis", "custom tts", "brand voice", "voice clone"],
    category: "Organization",
    path: "/organization/configurations#custom-voices",
    icon: "voice-over",
  },
  {
    id: "snippets",
    title: "Snippets",
    description: "Manage reusable code snippets",
    keywords: [
      "snippets",
      "code",
      "reusable",
      "templates",
      "blocks",
      "audio clips",
      "sounds",
      "pre-recorded",
      "hold music",
      "greeting audio",
      "audio snippets",
      "shared code",
    ],
    category: "Organization",
    path: "/organization/configurations#audio-snippets",
    icon: "snippet",
  },
  {
    id: "usage",
    title: "Usage",
    description: "View usage statistics",
    keywords: ["usage", "cost", "payment", "invoice", "billing", "spend", "minutes", "consumption", "monthly", "api calls"],
    category: "Organization",
    path: "/organization/usage",
    icon: "activity",
  },
  {
    id: "credits-billing",
    title: "Billing & Credits",
    description: "View credit balance and add funds",
    keywords: [
      "credits",
      "billing",
      "balance",
      "funds",
      "payment",
      "transactions",
      "charge",
      "cost",
      "pricing",
      "wallet",
      "top up",
      "add money",
      "receipt",
      "auto-pay",
      "stripe",
      "invoice",
    ],
    category: "Organization",
    path: "/organization/billing",
    icon: "payments",
  },
];
